<script lang="ts" setup>
import { onMounted, nextTick } from 'vue';
import hljs from 'highlight.js/lib/core';
import typescript from 'highlight.js/lib/languages/typescript';
import json from 'highlight.js/lib/languages/json';
import 'highlight.js/styles/github-dark.css';

hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('json', json);

onMounted(() =>
{
    nextTick(() =>
    {
        document.querySelectorAll('pre code').forEach((block) =>
        {
            hljs.highlightElement(block as HTMLElement);
        });
    });
});
</script>

<template>
    <section class="doc-section">
        <h2>
            {{ $t('[[[Skrypt dla triggera Client Side]]]') }}
        </h2>
        <p>{{ $t('[[[Skrypt pozwala na wykonywanie dowolnych akcji po stronie frontendu.]]]') }}</p>

        <!-- DOSTĘPNE ZMIENNE -->
        <details open>
            <summary>
                <i class="fa-solid fa-gears"></i>
                {{ $t('[[[Dostępne zmienne]]]') }}
            </summary>
            <div class="content">
                <ul class="var-list">
                    <li><b>Entry</b> – {{ $t('[[[Dane formularza]]]') }}</li>
                    <li><b>Form</b> – {{ $t('[[[Definicja formularza]]]') }}</li>
                    <li><b>Blueprint</b> – {{ $t('[[[Blueprint kontrolki uruchamiającej trigger]]]') }}</li>
                    <li><b>FormDOM</b> – {{ $t('[[[DOM formularza]]]') }}</li>
                    <li><b>Builder</b> – {{ $t('[[[Form builder - dostęp do schema, entry, itd.]]]') }}</li>
                    <li><b>Props</b> – {{ $t('[[[Parametry przekazane do formularza]]]') }}</li>
                    <li><b>SrvResults</b> – {{ $t('[[[Wyniki skryptów Server Side dla kontrolki]]]') }}</li>
                    <li><b>Values</b> – {{ $t('[[[Zmienne przekazane z konfiguracji]]]') }}</li>
                    <li><b>TableRowIndex</b> – {{ $t('[[[Index wiersza w tabeli w której znajduje się komponent uruchamiający trigger]]]') }}</li>
                    <li><b>Events</b> – {{ $t('[[[Dostęp do $events]]]') }}</li>
                    <li>
                        <b>Components</b> – {{ $t('[[[Dane komponentów formularza]]]') }}
                        <ul>
                            <li><b>ctx</b> – {{ $t('[[[Kontekst kontrolki]]]') }}</li>
                            <li><b>props</b> – {{ $t('[[[Właściwości przekazane do komponentu]]]') }}</li>
                        </ul>
                    </li>
                </ul>
            </div>
        </details>

        <!-- KONFIGURACJA PARAMETRÓW -->
        <details>
            <summary>
                <i class="fa-solid fa-sliders"></i>
                {{ $t('[[[Konfiguracja parametrów]]]') }}
            </summary>
            <div class="content">
                <p>{{ $t('[[[Do skryptu jest możliwość przekazywania parametrów poprzez wstawienie do sekcji Konfiguracja odpowiedniego json-a.]]]') }}</p>
                <pre><code class="json">[
	{
		"Key": "TextControlKey",
		"Name": "Kontrolka tekstowa",
		"Required": true,
		"Type": "text",
		"DefaultValue": "test"
	},
	{
		"Key": "ModuleControlKey",
		"Name": "Kontrolka modułowa",
		"Required": true,
		"Type": "module",
		"RelatedControls": [
			{
				"Key": "TextModuleControl",
				"Name": "Moduł - text",
				"Required": false,
				"Type": "text"
			}
		]
	}
]</code></pre>
                <p>{{ $t('[[[Wszystkie wartości ustawione w tej sekcji można nadpisać w konfiguracji skryptu.]]]') }}</p>
            </div>
        </details>

        <!-- PRZYKŁADY SKRYPTÓW -->
        <details>
            <summary>
                <i class="fa-solid fa-code-branch"></i>
                {{ $t('[[[Przykłady skryptów]]]') }}
            </summary>
            <div class="content">
                <div class="code-example">
                    <h4><i class="fa-solid fa-pen-to-square"></i> {{ $t('[[[Modyfikacja wartości w kontrolce tekstowej]]]') }}</h4>
                    <pre><code class="typescript">Entry.Text1.data = 'nowa wartość';</code></pre>
                </div>

                <div class="code-example">
                    <h4><i class="fa-regular fa-calendar-days"></i> {{ $t('[[[Ustawienie wartości w kontrolce typu data ze zmiennej]]]') }}</h4>
                    <pre><code class="typescript">Entry.Date1.data = Values.TestDateValue;</code></pre>
                </div>

                <div class="code-example">
                    <h4><i class="fa-solid fa-list"></i> {{ $t('[[[Nadpisanie opcji w kontrolce Użytkownik]]]') }}</h4>
                    <pre><code class="typescript">Components.User1.ctx.customOptions = [
    {
        "pictureUrl": "",
        "unit": "IT",
        "isActive": true,
        "key": "1",
        "value": "System Administrator"
    }
];</code></pre>
                </div>

                <div class="code-example">
                    <h4><i class="fa-solid fa-list"></i> {{ $t('[[[Nadpisanie opcji w kontrolce Słownik Logito na podstawie danych z innej kontrolki]]]') }}</h4>
                    <pre><code class="typescript">Components.LogitoDictionary1.ctx.customOptions = Entry.LogitoDictionary2.data;</code></pre>
                </div>
            </div>
        </details>
    </section>
</template>

<style scoped>
.doc-section {
  font-family: "Segoe UI", sans-serif;
  line-height: 1.6;
  color: #222;
}

.doc-section h2 {
  font-size: 1.4rem;
  border-bottom: 2px solid #007acc;
  padding-bottom: 4px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

details {
  margin: 0 0 0.5rem 0;
}

summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  font-weight: 600;
  font-size: 1.1rem;
  color: #007acc;
  border-bottom: 1px solid #ddd;
}

summary::-webkit-details-marker {
  display: none;
}

summary::after {
  content: '▸';
  transition: transform 0.2s;
}

details[open] summary::after {
  transform: rotate(90deg);
}

.content {
  padding: 0.75rem 1rem;
  background: #f8f9fb;
  border-left: 4px solid #007acc;
  border-radius: 0 0 6px 6px;
}

.var-list {
  list-style: none;
  padding-left: 0;
  margin: 0;
}

.var-list li {
  margin: 0.2rem 0;
}

pre {
  background: #1e1e1e;
  color: #f8f8f2;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  overflow-x: auto;
  margin: 0.5rem 0 1rem;
}

.code-example h4 {
  margin: 0.4rem 0;
  color: #444;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
</style>
